/**
 * VideoLightbox — fullscreen video viewer.
 *
 * Native HTML5 controls (play/pause/seek/volume/fullscreen/PiP/speed) —
 * работают надёжно на всех платформах (Chrome, Safari, iOS PWA, Android).
 *
 * Custom layer:
 *  - Close button (X)
 *  - Prev/Next arrows если onPrev/onNext заданы
 *  - Keyboard: Esc=close, Arrow=nav
 *  - Wheel/swipe → prev/next
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/prototype/Icon";
import { downloadChatAttachment } from "@/lib/downloadAttachment";

// Кнопки верхней панели лайтбокса. pill — с подписью (ПК), icon — круглая
// без подписи (телефон, чтобы четыре кнопки влезли в ширину экрана).
const pillBtnStyle: CSSProperties = {
  flex: "0 0 auto", height: 44, borderRadius: 22,
  background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
  cursor: "pointer", pointerEvents: "auto",
  display: "flex", alignItems: "center", justifyContent: "center",
  gap: 6, padding: "0 16px", fontSize: 14, fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const iconBtnStyle: CSSProperties = {
  ...pillBtnStyle, width: 44, padding: 0, borderRadius: "50%",
};

export function VideoLightbox({
  src, poster, filename, onClose, onPrev, onNext, hasPrev, hasNext, attachmentId, onSaveToDrive,
  onJump, jumpLabel,
}: {
  src: string;
  poster?: string;
  filename?: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  attachmentId?: number;
  onSaveToDrive?: (img: { attachmentId: number; filename?: string }) => void;
  onJump?: () => void;
  jumpLabel?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [speed, setSpeed] = useState(1);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  // Узкий экран (телефон): кнопки верхней панели без подписей, только иконки.
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Применяем выбранную скорость к video элементу
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
  }, [speed, src]);

  // Закрыть popup по клику вне
  useEffect(() => {
    if (!speedMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-speed-popup]")) return;
      setSpeedMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [speedMenuOpen]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Auto-play на mount
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.play().catch(() => {/* autoplay blocked — юзер сам жмёт play */});
    }
  }, [src]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowLeft" && onPrev && hasPrev) {
        if (e.shiftKey || e.ctrlKey || e.metaKey) return;  // seek backward
        e.preventDefault(); onPrev(); return;
      }
      if (e.key === "ArrowRight" && onNext && hasNext) {
        if (e.shiftKey || e.ctrlKey || e.metaKey) return;  // seek forward
        e.preventDefault(); onNext(); return;
      }
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        const v = videoRef.current;
        if (v) { if (v.paused) v.play().catch(() => {}); else v.pause(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Wheel/swipe navigation (debounced 350ms)
  const wheelLockRef = useRef<number>(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const dx = Math.abs(e.deltaX);
      const dy = Math.abs(e.deltaY);
      const delta = dx > dy ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 12) return;
      const now = Date.now();
      if (now - wheelLockRef.current < 350) return;
      wheelLockRef.current = now;
      if (delta > 0 && onNext && hasNext) { e.preventDefault(); onNext(); }
      else if (delta < 0 && onPrev && hasPrev) { e.preventDefault(); onPrev(); }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onPrev, onNext, hasPrev, hasNext]);

  // Swipe-down → close
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (dy > 100 && Math.abs(dy) > Math.abs(dx)) onClose();
    else if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0 && onPrev && hasPrev) onPrev();
      else if (dx < 0 && onNext && hasNext) onNext();
    }
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.92)",
        zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Верхняя панель одной flex-строкой: имя файла слева, справа —
          скорость / скачать / на диск / закрыть. Раньше кнопки и плашка
          имени были спозиционированы абсолютно и на телефоне наезжали
          друг на друга. На узком экране кнопки — только иконки. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute", top: 16, left: 16, right: 16,
          display: "flex", alignItems: "center", gap: 8,
          zIndex: 10001, pointerEvents: "none",
        }}
      >
        {filename ? (
          <div
            style={{
              flex: "1 1 auto", minWidth: 0,
              color: "#fff", background: "rgba(0,0,0,0.5)",
              padding: "8px 14px", borderRadius: 12,
              fontSize: 13, fontWeight: 500,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              pointerEvents: "auto",
            }}
          >
            {filename}
          </div>
        ) : (
          <div style={{ flex: "1 1 auto" }}/>
        )}

        {/* Speed control */}
        <div data-speed-popup style={{ position: "relative", flex: "0 0 auto", pointerEvents: "auto" }}>
          <button
            type="button"
            onClick={() => setSpeedMenuOpen(v => !v)}
            title="Скорость воспроизведения"
            style={{
              height: 44, minWidth: 56, borderRadius: 22,
              background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 6, padding: "0 14px", fontSize: 14, fontFamily: "inherit",
              fontWeight: 600,
            }}
          >
            {speed === 1 ? "1×" : `${speed}×`}
          </button>
          {speedMenuOpen && (
            <div
              style={{
                position: "absolute", top: 50, right: 0,
                background: "rgba(20,20,20,0.95)", color: "#fff",
                borderRadius: 12, padding: 6,
                minWidth: 100,
                display: "flex", flexDirection: "column", gap: 2,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                backdropFilter: "blur(8px)",
              }}
            >
              {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setSpeed(s); setSpeedMenuOpen(false); }}
                  style={{
                    background: s === speed ? "rgba(79,174,78,0.25)" : "transparent",
                    color: s === speed ? "#7bc67e" : "#fff",
                    border: "none", padding: "8px 14px",
                    borderRadius: 8, cursor: "pointer",
                    fontSize: 14, fontWeight: s === speed ? 700 : 500,
                    textAlign: "left", fontFamily: "inherit",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  {s === 1 ? "Норм" : `${s}×`}
                  {s === speed && <Icon name="check" size={14}/>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Скачать: ПК — <a download> с ?download=1 (Content-Disposition:
            attachment), телефон — Web Share («Сохранить видео» на iOS). */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void downloadChatAttachment({ attachmentId, url: src, filename });
          }}
          title="Скачать видео"
          style={narrow ? iconBtnStyle : pillBtnStyle}
        >
          <Icon name="download" size={18}/>
          {!narrow && "Скачать"}
        </button>

        {/* Save-to-drive */}
        {onSaveToDrive && attachmentId != null && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSaveToDrive({ attachmentId, filename }); }}
            title="Добавить на диск"
            style={narrow ? iconBtnStyle : pillBtnStyle}
          >
            <Icon name="folder" size={18}/>
            {!narrow && "Добавить на диск"}
          </button>
        )}

        {/* Close */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          title="Закрыть (Esc)"
          style={iconBtnStyle}
        >
          <Icon name="x" size={22}/>
        </button>
      </div>

      {/* Jump button (например "Перейти к папке" из DriveView фильтра) */}
      {onJump && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onJump(); onClose(); }}
          style={{
            position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.5)", color: "#fff",
            border: "none", padding: "8px 18px", borderRadius: 16,
            fontSize: 13, cursor: "pointer", zIndex: 10001,
            pointerEvents: "auto",
          }}
        >
          {jumpLabel ?? "Перейти"}
        </button>
      )}

      {/* Prev */}
      {onPrev && hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          title="Назад (←)"
          style={{
            position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: "50%",
            background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
            cursor: "pointer", zIndex: 10001,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="chevron_l" size={28}/>
        </button>
      )}

      {/* Next */}
      {onNext && hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          title="Вперёд (→)"
          style={{
            position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: "50%",
            background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
            cursor: "pointer", zIndex: 10001,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="chevron_r" size={28}/>
        </button>
      )}

      {/* Video — native controls (play/pause/seek/volume/fullscreen/PiP/speed).
          preload="metadata": тянем только заголовки/индекс (быстро). Дальше
          браузер сам подгрузит через Range по мере воспроизведения. */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        preload="metadata"
        playsInline
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          background: "#000",
        }}
      />
    </div>,
    document.body,
  );
}
